/**
 * InstantMessagingChat — shared 2-column chat layout reused by:
 *   1. InstantMessagingDrawer (mini float panel, 380px)
 *   2. MessagerieInstantanee dedicated page (full screen)
 *
 * Inter-PS only. Attachments are blocked by design (HDS): clicking the upload
 * affordance shows the mandated message redirecting to MSSanté webmail.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../apiBase";
import { useIm } from "./ImProvider";
import { useProAuth } from "../components/ProAuthContext";
import {
  Search,
  Send,
  Paperclip,
  BellOff,
  Bell,
  CheckCheck,
  Check,
  Plus,
  X,
  MessageSquare,
  Wifi,
  WifiOff,
  Maximize2,
  Minimize2,
} from "lucide-react";

const HDS_BLOCK_MESSAGE = "Pour les documents médicaux, utilisez la Messagerie MSSanté";

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return d.toLocaleDateString("fr-FR", { weekday: "short" });
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// ----------------- Compose: new conversation picker ------------------------
function NewConversationPicker({ onSelect, onCancel }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/im/users`)
      .then(({ data }) => alive && setUsers(Array.isArray(data) ? data : []))
      .catch(() => alive && setUsers([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(needle) ||
        (u.email || "").toLowerCase().includes(needle) ||
        (u.role || "").toLowerCase().includes(needle)
    );
  }, [users, q]);

  return (
    <div className="flex flex-col h-full" data-testid="im-new-conversation">
      <div className="p-3 border-b border-[rgba(127,179,255,0.18)] flex items-center gap-2">
        <button
          onClick={onCancel}
          data-testid="im-new-conversation-cancel"
          className="text-[12px] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1"
        >
          <X size={14} /> Retour
        </button>
        <div className="text-[12px] text-[#0F2B5B] font-medium">Démarrer une conversation</div>
      </div>
      <div className="p-3 border-b border-[rgba(127,179,255,0.12)]">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mc-muted)]" />
          <input
            type="text"
            placeholder="Rechercher un professionnel…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="im-new-conversation-search"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-[#F8FAFC] border border-[rgba(127,179,255,0.22)] text-[#0F2B5B] text-[13px] placeholder:text-[var(--mc-muted)] outline-none focus:border-[var(--mc-blue-400)]"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && <div className="p-5 text-[12px] text-[var(--mc-muted)]">Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-5 text-[12px] text-[var(--mc-muted)]">Aucun professionnel trouvé.</div>
        )}
        {filtered.map((u) => (
          <button
            key={u.id}
            onClick={() => onSelect(u.id)}
            data-testid={`im-pick-user-${u.id}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[rgba(45,123,255,0.08)] transition border-b border-[rgba(127,179,255,0.06)]"
          >
            <div className="relative shrink-0">
              <div className="h-9 w-9 rounded-full bg-[rgba(110,231,197,0.14)] border border-[rgba(110,231,197,0.32)] text-[var(--mc-mint)] grid place-items-center text-[11px] font-semibold">
                {initials(u.name)}
              </div>
              {u.online && (
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--mc-mint)] border-2 border-[var(--mc-navy-950)]" />
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[#0F2B5B] text-[13px] font-medium truncate">{u.name}</div>
              <div className="text-[11px] text-[var(--mc-muted)] truncate">{u.role}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ----------------- Conversation list ---------------------------------------
function ConversationList({ conversations, activeId, onlineUsers, onSelect, onCompose, dnd, onToggleDnd, connected }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-[rgba(127,179,255,0.18)] flex items-center gap-2">
        <div className="text-[13px] text-[#0F2B5B] font-semibold flex-1 inline-flex items-center gap-2">
          <MessageSquare size={14} className="text-[var(--mc-mint)]" />
          Messagerie instantanée
        </div>
        <span
          title={connected ? "Connecté" : "Reconnexion…"}
          className={`inline-flex items-center text-[10px] ${connected ? "text-[var(--mc-mint)]" : "text-[#B45309]"}`}
          data-testid="im-connection-status"
        >
          {connected ? <Wifi size={12} /> : <WifiOff size={12} />}
        </span>
        <button
          onClick={onToggleDnd}
          title={dnd ? "Ne pas déranger activé" : "Ne pas déranger"}
          data-testid="im-toggle-dnd"
          aria-pressed={dnd}
          data-state={dnd ? "on" : "off"}
          className={`grid place-items-center h-7 w-7 rounded-md border text-[11px] transition ${
            dnd
              ? "border-[rgba(255,170,80,0.45)] text-[#B45309] bg-[rgba(255,170,80,0.10)]"
              : "border-[rgba(127,179,255,0.22)] text-[#0284C7] hover:border-[var(--mc-blue-400)]"
          }`}
        >
          {dnd ? <BellOff size={13} /> : <Bell size={13} />}
        </button>
        <button
          onClick={onCompose}
          title="Nouvelle conversation"
          data-testid="im-compose"
          className="grid place-items-center h-7 w-7 rounded-md border border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] bg-[rgba(110,231,197,0.10)] hover:bg-[rgba(110,231,197,0.18)]"
        >
          <Plus size={13} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="im-conversation-list">
        {conversations.length === 0 && (
          <div className="p-5 text-[12px] text-[var(--mc-muted)] leading-relaxed">
            Aucune conversation pour l&apos;instant.
            <button
              onClick={onCompose}
              className="block mt-2 text-[var(--mc-mint)] underline hover:no-underline"
            >
              Démarrer une discussion avec un confrère
            </button>
          </div>
        )}
        {conversations.map((c) => {
          const isActive = c.id === activeId;
          const online = c.other_user_id && onlineUsers.has(c.other_user_id);
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              data-testid={`im-conv-${c.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-[rgba(127,179,255,0.06)] text-left transition ${
                isActive ? "bg-[rgba(45,123,255,0.18)]" : "hover:bg-[rgba(45,123,255,0.06)]"
              }`}
            >
              <div className="relative shrink-0">
                <div className="h-10 w-10 rounded-full bg-[rgba(110,231,197,0.14)] border border-[rgba(110,231,197,0.32)] text-[var(--mc-mint)] grid place-items-center text-[12px] font-semibold">
                  {initials(c.other_name)}
                </div>
                {online && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--mc-mint)] border-2 border-[var(--mc-navy-950)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[#0F2B5B] text-[13px] font-medium truncate flex-1">{c.other_name || "Confrère"}</div>
                  <div className="text-[10px] text-[var(--mc-muted)] shrink-0">{relativeTime(c.last_message_at)}</div>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className={`text-[11.5px] truncate flex-1 ${c.unread > 0 ? "text-[#0F2B5B] font-medium" : "text-[var(--mc-muted)]"}`}>
                    {c.last_message_preview || "Aucun message"}
                  </div>
                  {c.unread > 0 && (
                    <span
                      data-testid={`im-conv-unread-${c.id}`}
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[var(--mc-mint)] text-[var(--mc-navy-950)] min-w-[18px] text-center"
                    >
                      {c.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ----------------- Thread / Composer ---------------------------------------
function ChatThread({ conversation, messages, meId, onlineUsers, onSend, onBack, compact }) {
  const [draft, setDraft] = useState("");
  const [hdsBlock, setHdsBlock] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, conversation?.id]);

  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="im-thread-empty">
        <MessageSquare size={32} className="text-[var(--mc-blue-300)] mb-3" />
        <div className="text-[#0F2B5B] text-[14px] font-medium">Sélectionnez une conversation</div>
        <p className="text-[12px] text-[var(--mc-muted)] mt-1 max-w-xs">
            Ou démarrez-en une nouvelle pour échanger en temps réel avec un confrère.
        </p>
      </div>
    );
  }

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await onSend(conversation.id, draft);
      setDraft("");
    } finally {
      setSending(false);
    }
  };

  const handleAttachClick = (e) => {
    e.preventDefault();
    setHdsBlock(true);
    // Reset the file input just in case (file picker never opens because we preventDefault).
    if (fileInputRef.current) fileInputRef.current.value = "";
    setTimeout(() => setHdsBlock(false), 6000);
  };

  const online = conversation.other_user_id && onlineUsers.has(conversation.other_user_id);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[rgba(127,179,255,0.18)] flex items-center gap-3">
        {compact && onBack && (
          <button onClick={onBack} className="text-[#0284C7] hover:text-[#0F2B5B] text-[12px]" data-testid="im-thread-back">
            <X size={16} />
          </button>
        )}
        <div className="relative">
          <div className="h-9 w-9 rounded-full bg-[rgba(110,231,197,0.14)] border border-[rgba(110,231,197,0.32)] text-[var(--mc-mint)] grid place-items-center text-[11px] font-semibold">
            {initials(conversation.other_name)}
          </div>
          {online && (
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--mc-mint)] border-2 border-[var(--mc-navy-950)]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[#0F2B5B] text-[13.5px] font-semibold truncate">{conversation.other_name}</div>
          <div className="text-[11px] text-[var(--mc-muted)] truncate">
            {online ? "En ligne" : "Hors ligne"} · {conversation.other_role || "Professionnel de santé"}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="im-thread-messages">
        {messages.length === 0 && (
          <div className="text-center text-[12px] text-[var(--mc-muted)] mt-6">
            Échangez en temps réel.
            <div className="text-[11px] mt-1">
              Inter-PS uniquement — pour le clinique, utilisez la <strong className="text-[#0F2B5B]">Messagerie MSSanté</strong>.
            </div>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === meId;
          const peerRead = mine && (m._peer_read || (m.read_by || []).some((r) => r.user_id && r.user_id !== meId));
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`im-message-${m.id}`}
                className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug shadow-sm ${
                  mine
                    ? "bg-[var(--mc-blue-400)] text-white rounded-br-sm"
                    : "bg-[#F1F5F9] text-[#0F2B5B] rounded-bl-sm border border-[rgba(127,179,255,0.18)]"
                }`}
              >
                <div className="whitespace-pre-wrap break-words">{m.content}</div>
                <div className={`mt-0.5 text-[9.5px] flex items-center gap-1 ${mine ? "text-white/75 justify-end" : "text-[var(--mc-muted)]"}`}>
                  <span>{relativeTime(m.created_at)}</span>
                  {mine && (peerRead
                    ? <CheckCheck size={11} className="text-white/95" />
                    : <Check size={11} className="text-white/70" />)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* HDS block alert */}
      {hdsBlock && (
        <div
          data-testid="im-hds-block-banner"
          className="mx-4 mb-2 rounded-lg border border-[rgba(255,170,80,0.45)] bg-[rgba(255,170,80,0.10)] text-[#B45309] text-[12px] px-3 py-2 leading-snug"
        >
          {HDS_BLOCK_MESSAGE}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={handleSend}
        className="border-t border-[rgba(127,179,255,0.18)] p-3 flex items-end gap-2"
      >
        <button
          type="button"
          onClick={handleAttachClick}
          data-testid="im-attach-blocked"
          title={HDS_BLOCK_MESSAGE}
          className="grid place-items-center h-9 w-9 rounded-md border border-[rgba(127,179,255,0.22)] text-[#0284C7] hover:text-[#0F2B5B] hover:border-[var(--mc-blue-400)] shrink-0"
        >
          <Paperclip size={15} />
        </button>
        {/* Hidden input — never reached because button preventDefaults. */}
        <input ref={fileInputRef} type="file" className="hidden" />
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={1}
          placeholder="Écrivez un message…"
          data-testid="im-composer-input"
          className="flex-1 resize-none px-3 py-2 rounded-md bg-[#F8FAFC] border border-[rgba(127,179,255,0.22)] text-[#0F2B5B] text-[13px] placeholder:text-[var(--mc-muted)] outline-none focus:border-[var(--mc-blue-400)] max-h-32"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending}
          data-testid="im-send-button"
          className="grid place-items-center h-9 w-9 rounded-md bg-[var(--mc-mint)] text-[var(--mc-navy-950)] disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}

// ----------------- High-level layout ---------------------------------------
export default function InstantMessagingChat({ variant = "page", onClose, onTogglePage }) {
  const { user } = useProAuth();
  const im = useIm();
  const [composing, setComposing] = useState(false);
  // For drawer-on-mobile: when a conversation is selected, hide the list.
  const [showThreadOnly, setShowThreadOnly] = useState(false);

  const activeConv = useMemo(
    () => im.conversations.find((c) => c.id === im.activeConversationId) || null,
    [im.conversations, im.activeConversationId]
  );

  const handleSelect = (cid) => {
    im.setActiveConversationId(cid);
    im.loadMessages(cid);
    setShowThreadOnly(true);
    setComposing(false);
  };

  const handlePickUser = async (uid) => {
    try {
      const cid = await im.openConversation(uid);
      im.setActiveConversationId(cid);
      im.loadMessages(cid);
      setComposing(false);
      setShowThreadOnly(true);
    } catch (e) {
      // ignore
    }
  };

  const isDrawer = variant === "drawer";

  return (
    <div
      data-testid={`im-chat-${variant}`}
      className={`flex h-full min-h-0 ${isDrawer ? "" : "rounded-xl border border-[rgba(127,179,255,0.18)] bg-white overflow-hidden"}`}
    >
      {/* Left column: list (always visible on page, hidden when thread shown in drawer) */}
      <aside
        className={`${isDrawer ? (showThreadOnly ? "hidden" : "flex w-full") : "hidden lg:flex w-[280px] border-r border-[rgba(127,179,255,0.18)]"} flex-col min-h-0`}
      >
        {composing ? (
          <NewConversationPicker
            onSelect={handlePickUser}
            onCancel={() => setComposing(false)}
          />
        ) : (
          <ConversationList
            conversations={im.conversations}
            activeId={im.activeConversationId}
            onlineUsers={im.onlineUsers}
            onSelect={handleSelect}
            onCompose={() => setComposing(true)}
            dnd={im.dnd}
            onToggleDnd={im.toggleDnd}
            connected={im.connected}
          />
        )}
        {isDrawer && onTogglePage && !composing && (
          <button
            onClick={onTogglePage}
            data-testid="im-open-fullscreen"
            className="m-3 inline-flex items-center justify-center gap-1.5 text-[11px] text-[#0284C7] hover:text-[#0F2B5B] border border-[rgba(127,179,255,0.22)] rounded-md py-1.5"
          >
            <Maximize2 size={12} /> Ouvrir en plein écran
          </button>
        )}
      </aside>

      {/* Right column: thread */}
      <section
        className={`${isDrawer ? (showThreadOnly ? "flex w-full" : "hidden") : "flex flex-1"} flex-col min-h-0`}
      >
        <ChatThread
          conversation={activeConv}
          messages={im.messagesByConv[im.activeConversationId] || []}
          meId={user?.id}
          onlineUsers={im.onlineUsers}
          onSend={im.sendMessage}
          onBack={isDrawer ? () => setShowThreadOnly(false) : undefined}
          compact={isDrawer}
        />
        {isDrawer && activeConv && onTogglePage && (
          <button
            onClick={onTogglePage}
            data-testid="im-open-fullscreen-thread"
            className="m-3 inline-flex items-center justify-center gap-1.5 text-[11px] text-[#0284C7] hover:text-[#0F2B5B] border border-[rgba(127,179,255,0.22)] rounded-md py-1.5"
          >
            <Maximize2 size={12} /> Ouvrir en plein écran
          </button>
        )}
      </section>
    </div>
  );
}

export { HDS_BLOCK_MESSAGE };
